import { useEffect, useRef, useState } from 'react';
import type { GameView } from '@champdle/contracts';
import { avatarUrl } from '../api.js';
import { formatDuration, shareText } from '../board.js';
import type { ChampionItem } from './GuessInput.js';

interface Props {
  game: GameView;
  answer: ChampionItem | undefined;
  onPlayAgain: () => void;
  busy: boolean;
}

type CopyState = 'idle' | 'copied' | 'manual';

export function ResultPanel({ game, answer, onPlayAgain, busy }: Props) {
  const won = game.status === 'won';
  const n = game.guesses.length;
  const [copy, setCopy] = useState<CopyState>('idle');
  const manualRef = useRef<HTMLTextAreaElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const text = shareText(game.guesses, {
    won,
    elapsedMs: game.elapsedMs,
    url: window.location.origin,
  });

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    if (copy === 'manual') manualRef.current?.select();
  }, [copy]);

  const share = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopy('copied');
      window.setTimeout(() => setCopy('idle'), 2500);
    } catch {
      setCopy('manual');
    }
  };

  return (
    <section className="result" aria-labelledby="result-h">
      <h2 id="result-h" tabIndex={-1} ref={headingRef}>
        {won ? 'Solved!' : 'Better luck next time'}
      </h2>
      {answer ? (
        <p className="result__answer">
          <img src={avatarUrl(answer.avatar)} alt="" width={64} height={64} />
          <span>
            The champion was <strong>{answer.name}</strong>.
          </span>
        </p>
      ) : null}
      <p className="muted result__stats">
        <span>
          {n} {n === 1 ? 'guess' : 'guesses'}
        </span>
        <span aria-hidden="true">·</span>
        <span>{formatDuration(game.elapsedMs)}</span>
      </p>
      <div className="result__buttons">
        <button type="button" className="primary" onClick={onPlayAgain} disabled={busy}>
          Play again
        </button>
        {n > 0 ? (
          <button type="button" onClick={() => void share()}>
            Share result
          </button>
        ) : null}
      </div>
      <div role="status" className="result__copied">
        {copy === 'copied' ? 'Copied to clipboard.' : ''}
      </div>
      {copy === 'manual' ? (
        <label className="result__manual">
          Copy this text to share it
          <textarea ref={manualRef} readOnly rows={n + 4} value={text} />
        </label>
      ) : null}
    </section>
  );
}
